import React, { useMemo, useState } from 'react';
import moment from 'moment';
import {
    Button,
    Col,
    FormGroup,
    InputGroup,
    InputGroupText,
    Label,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from 'reactstrap';
import { Field, Form, Input } from '@availity/form';
import * as yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import dateLogo from '../../../../../assets/knowledgecontent/dateLogo.png';
import Rodal from '../../../../../components/Rodal/Rodal';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import { useStateSelector } from '../../../../../store/selectors';
import createNotification from '../../../../../utils/createNotification';
import {
    FaqAttachmentType,
    FaqImagePosition,
    FaqParty,
    FaqServiceType,
    FaqStatus,
    GetFaqItemDto,
} from '../models/faq-item.model';
import './FaqItemModal.scss';

interface FaqItemModalProps {
    isVisible: boolean;
    onClose: () => void;
    onSaved: () => void;
    item?: GetFaqItemDto;
    mode?: 'faq' | 'video';
}

const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg';
const ALLOWED_IMAGE_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/webp',
    'image/svg+xml',
];

const VIDEO_ACCEPT = '.mp4,.mov,.avi,.wmv,.webm,.mkv,.m4v';
const ALLOWED_VIDEO_MIME_TYPES = [
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
    'video/x-ms-wmv',
    'video/webm',
    'video/x-matroska',
];

export const FaqItemModal = ({
    isVisible,
    onClose,
    onSaved,
    item,
    mode = 'faq',
}: FaqItemModalProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const isEdit = !!item;
    const isVideo = mode === 'video';

    const [isSaving, setIsSaving] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState(item?.fileName ?? '');
    const [fileRemoved, setFileRemoved] = useState(false);
    const [imagePosition, setImagePosition] = useState<FaqImagePosition>(
        item?.imagePosition ?? FaqImagePosition.Right
    );
    const [serviceType, setServiceType] = useState<FaqServiceType>(
        item?.serviceType ?? FaqServiceType.All
    );
    const [party, setParty] = useState<FaqParty>(
        item?.party ?? FaqParty.All
    );
    const [status, setStatus] = useState<FaqStatus>(
        item?.status ?? FaqStatus.Draft
    );

    const expectedAttachmentType = isVideo
        ? FaqAttachmentType.Video
        : FaqAttachmentType.Image;

    const hasExistingFile =
        isEdit &&
        item.attachmentType === expectedAttachmentType &&
        !!item.fileName &&
        !fileRemoved;

    const attachmentType = isVideo
        ? FaqAttachmentType.Video
        : file || hasExistingFile
        ? FaqAttachmentType.Image
        : FaqAttachmentType.None;

    const showImagePosition =
        !isVideo &&
        attachmentType === FaqAttachmentType.Image &&
        (hasExistingFile || !!file);

    const validationSchema = useMemo(
        () =>
            yup.object().shape({
                question: yup.string().required(),
                answer: isVideo
                    ? yup.string().optional()
                    : yup.string().required(),
            }),
        [isVideo]
    );

    let modalForm: any = {};

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files?.[0] ?? null;
        const allowedTypes = isVideo
            ? ALLOWED_VIDEO_MIME_TYPES
            : ALLOWED_IMAGE_MIME_TYPES;
        if (selected && !allowedTypes.includes(selected.type)) {
            createNotification(
                isVideo
                    ? 'Only video files are allowed.'
                    : 'Only image files are allowed.',
                'error'
            );
            event.target.value = '';
            return;
        }
        setFile(selected);
        setFileName(selected?.name ?? '');
        setFileRemoved(false);
        modalForm.setFieldValue('sourceFile', selected?.name ?? '');
    };

    const handleRemoveFile = () => {
        setFile(null);
        setFileName('');
        setFileRemoved(true);
        modalForm.setFieldValue('sourceFile', '');
    };

    const initSubmit = () => {
        modalForm.handleSubmit();
    };

    const handleSave = async () => {
        if (isVideo && !file && !hasExistingFile) {
            createNotification('Please select a video file.', 'error');
            return;
        }

        setIsSaving(true);
        try {
            const dto = {
                ...(isEdit ? { faqItemId: item.faqItemId } : {}),
                question: modalForm.values.question,
                answer: modalForm.values.answer,
                serviceType,
                party,
                status,
                attachmentType,
                imagePosition:
                    attachmentType === FaqAttachmentType.Image
                        ? imagePosition
                        : null,
            };

            const formData = new FormData();
            if (file) {
                formData.append('file', file);
            }
            formData.append('dto', JSON.stringify(dto));

            const response = await axios.post(
                '/api/MarketplaceFaq/admin/save',
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (!response.data?.isError) {
                const label = isVideo ? 'Video' : 'FAQ item';
                createNotification(
                    `${label} has been successfully ${isEdit ? 'updated' : 'saved'}!`,
                    'success'
                );
                onSaved();
                onClose();
            }
        } catch {
            createNotification(
                'Could not save. Please try again.',
                'error'
            );
        } finally {
            setIsSaving(false);
        }
    };

    const modalTitle = isEdit
        ? isVideo
            ? 'Edit Video'
            : 'Edit FAQ Item'
        : isVideo
        ? 'Video Details'
        : 'FAQ Item Details';

    return (
        <Rodal
            visible={isVisible}
            onClose={onClose}
            animation="fade"
            showMask={true}
            className="modal-style faq-item-modal-container">
            <div className="body_white_box associate_member faq-item-modal">
                <ModalHeader>{modalTitle}</ModalHeader>
                <ModalBody>
                    <Form
                        innerRef={(c) => {
                            modalForm = c;
                        }}
                        onSubmit={handleSave}
                        initialValues={{
                            question: item?.question ?? '',
                            answer: item?.answer ?? '',
                            sourceFile: item?.fileName ?? '',
                        }}
                        validationSchema={validationSchema}>
                        <div
                            className="associate_form announcement_form"
                            style={{ borderBottom: 0 }}>
                            <Row>
                                <Col md={12}>
                                    <FormGroup>
                                        <Field
                                            type="text"
                                            label={isVideo ? 'Title' : 'Question'}
                                            name="question"
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md={12}>
                                    <FormGroup>
                                        <Field
                                            type="textarea"
                                            label={isVideo ? 'Description' : 'Answer'}
                                            name="answer"
                                            style={{ minHeight: '115px' }}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md={12}>
                                    <FormGroup>
                                        <Label for="faq-upload-file">
                                            {isVideo ? 'Video File' : 'Source File'}
                                        </Label>
                                        <InputGroup>
                                            <InputGroupText className="upload-file-group prepend-addon">
                                                <Label
                                                    for="faq-upload-file"
                                                    className="choose-file-label">
                                                    Choose File
                                                </Label>
                                                <input
                                                    type="file"
                                                    id="faq-upload-file"
                                                    accept={
                                                        isVideo
                                                            ? VIDEO_ACCEPT
                                                            : IMAGE_ACCEPT
                                                    }
                                                    className="upload-file-input"
                                                    onChange={handleFileChange}
                                                />
                                            </InputGroupText>
                                            <div className="file-name-wrapper">
                                                <Input
                                                    type="text"
                                                    name="sourceFile"
                                                    value={fileName}
                                                    disabled={true}
                                                    style={{
                                                        backgroundColor: '#fff',
                                                    }}
                                                />
                                                {!isVideo &&
                                                    (file || hasExistingFile) && (
                                                        <FontAwesomeIcon
                                                            icon={faTrashCan}
                                                            className="remove-file-icon"
                                                            title="Remove file"
                                                            onClick={
                                                                handleRemoveFile
                                                            }
                                                        />
                                                    )}
                                            </div>
                                        </InputGroup>
                                    </FormGroup>
                                </Col>

                                {showImagePosition && (
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="faq-image-position">
                                                Position
                                            </Label>
                                            <div className="customerStyle">
                                                <select
                                                    id="faq-image-position"
                                                    value={imagePosition}
                                                    onChange={(e) =>
                                                        setImagePosition(
                                                            Number(
                                                                e.target.value
                                                            ) as FaqImagePosition
                                                        )
                                                    }>
                                                    <option
                                                        value={
                                                            FaqImagePosition.Right
                                                        }>
                                                        Right
                                                    </option>
                                                    <option
                                                        value={
                                                            FaqImagePosition.Top
                                                        }>
                                                        Top
                                                    </option>
                                                    <option
                                                        value={
                                                            FaqImagePosition.Bottom
                                                        }>
                                                        Bottom
                                                    </option>
                                                    <option
                                                        value={
                                                            FaqImagePosition.Left
                                                        }>
                                                        Left
                                                    </option>
                                                </select>
                                            </div>
                                        </FormGroup>
                                    </Col>
                                )}

                                <Col md={6} xs={12}>
                                    <FormGroup>
                                        <Label for="faq-service-type">
                                            Service Type
                                        </Label>
                                        <div className="customerStyle">
                                            <select
                                                id="faq-service-type"
                                                value={serviceType}
                                                onChange={(e) =>
                                                    setServiceType(
                                                        Number(
                                                            e.target.value
                                                        ) as FaqServiceType
                                                    )
                                                }>
                                                <option
                                                    value={FaqServiceType.All}>
                                                    All
                                                </option>
                                                <option
                                                    value={FaqServiceType.DB}>
                                                    Discount Brokerage
                                                </option>
                                                <option
                                                    value={FaqServiceType.FSB}>
                                                    Full-Service Brokerage
                                                </option>
                                                <option
                                                    value={FaqServiceType.SFAS}>
                                                    SFAS
                                                </option>
                                            </select>
                                        </div>
                                    </FormGroup>
                                </Col>

                                <Col md={6} xs={12}>
                                    <FormGroup>
                                        <Label for="faq-party">Party</Label>
                                        <div className="customerStyle">
                                            <select
                                                id="faq-party"
                                                value={party}
                                                onChange={(e) =>
                                                    setParty(
                                                        Number(
                                                            e.target.value
                                                        ) as FaqParty
                                                    )
                                                }>
                                                <option value={FaqParty.All}>
                                                    All
                                                </option>
                                                <option
                                                    value={FaqParty.Seller}>
                                                    Seller
                                                </option>
                                                <option value={FaqParty.Buyer}>
                                                    Buyer
                                                </option>
                                            </select>
                                        </div>
                                    </FormGroup>
                                </Col>

                                <Col md={12}>
                                    <FormGroup>
                                        <Label for="faq-status">Status</Label>
                                        <div className="customerStyle">
                                            <select
                                                id="faq-status"
                                                value={status}
                                                onChange={(e) =>
                                                    setStatus(
                                                        Number(e.target.value) as FaqStatus
                                                    )
                                                }>
                                                <option value={FaqStatus.Draft}>Draft</option>
                                                <option value={FaqStatus.Active}>Active</option>
                                            </select>
                                        </div>
                                    </FormGroup>
                                </Col>

                                {isEdit && (
                                    <>
                                        <Col md={6} xs={12}>
                                            <FormGroup>
                                                <Label>Originally Posted</Label>
                                                <InputGroup>
                                                    <InputGroupText className="prepend-addon">
                                                        <img src={dateLogo} alt="" />
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        name="dateCreated"
                                                        value={item.dateCreated ? moment(item.dateCreated).format('MM/D/YYYY h:mma') : ''}
                                                        disabled={true}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6} xs={12}>
                                            <FormGroup>
                                                <Label>Last Edited</Label>
                                                <InputGroup>
                                                    <InputGroupText className="prepend-addon">
                                                        <img src={dateLogo} alt="" />
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        name="dateModified"
                                                        value={item.dateModified ? moment(item.dateModified).format('MM/D/YYYY h:mma') : ''}
                                                        disabled={true}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        </div>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <div className="footer-wrapper-container">
                        <Button className="cancel" onClick={onClose}>
                            Cancel
                        </Button>
                        <ButtonLoader
                            buttonText="Save"
                            loaderButtonText="Saving"
                            disabled={false}
                            isLoading={isSaving}
                            onClick={initSubmit}
                            className={
                                isSaving
                                    ? 'btn btn-primary loader-submit'
                                    : 'btn btn-primary loader-submit save'
                            }
                        />
                    </div>
                </ModalFooter>
            </div>
        </Rodal>
    );
};
